import { DeviceItem } from '@/stores'
import { useLocaleStore } from '@/stores'
import { isElectron } from '@/utils/env'
import { isMacOS } from '@/utils/ua'
import { getReportRateSlot } from '@/views/device/mouse/composables/usePerformance'
import { MousePerfCfg, ParametersCapability, SensorMode } from '@rapoo/device-sdk'
import { computed, ref } from 'vue'

export function useCfgData(itemRef: { value: DeviceItem }) {
  const { t } = useLocaleStore()
  const isElectronEnv = isElectron()

  const device = computed(() => itemRef.value.original)
  const features = computed(() => device.value.features)

  const cfg = ref<MousePerfCfg>()
  const sensorMode = ref<SensorMode>()
  const getAllPerfCfg = async () => {
    const mousePerfCfg = features.value.mousePerfCfg

    if (mousePerfCfg) {
      cfg.value = await mousePerfCfg.getAllPerfCfg()
      if (cfg.value.reportRate !== undefined) {
        const slot = getReportRateSlot(mousePerfCfg, cfg.value.reportRate)
        sensorMode.value = await mousePerfCfg.getSensorMode(slot)
      }
    }
  }

  const pointerSpeed = ref()
  const getMousePointerSpeed = async () => {
    if (!isElectronEnv) return
    pointerSpeed.value = await window.electronAPI?.getMousePointerSpeed()
  }

  const wheelSpeed = ref()
  const getMouseWheelSpeed = async () => {
    if (!isElectronEnv) return
    wheelSpeed.value = await window.electronAPI?.getMouseWheelSpeed()
  }

  const initCfgData = async () => {
    await getAllPerfCfg()
    await getMousePointerSpeed()
    await getMouseWheelSpeed()
  }

  const cfgData = computed(() => {
    if (device.value.manifest.type !== 'mouse') {
      return null
    }
    if (!cfg.value) {
      return null
    }

    const data = []

    // 回报率
    if (cfg.value.reportRate !== undefined) {
      const map = {
        0x08: '125Hz',
        0x04: '250Hz',
        0x02: '500Hz',
        0x01: '1000Hz',
        0x84: '2000Hz',
        0x82: '4000Hz',
        0x81: '8000Hz',
      } as Record<number, string>
      if (cfg.value.reportRate in map) {
        data.push({
          name: t('home.overview.cfgData.reportRate'),
          value: map[cfg.value.reportRate],
        })
      }
    }

    // 传感器模式
    if (sensorMode.value !== undefined) {
      const modeMap = {
        0x00: t('home.overview.cfgData.sensorModeOffice'),
        0x01: t('home.overview.cfgData.sensorModeBalanced'),
        0x02: t('home.overview.cfgData.sensorModePower'),
        0x03: t('home.overview.cfgData.sensorModeHyper'),
        0x04: t('home.overview.cfgData.sensorModeCompetition'),
        0x05: t('home.overview.cfgData.sensorModeRampage'),
      } as Record<number, string>
      if (sensorMode.value in modeMap) {
        data.push({
          name: t('home.overview.cfgData.sensorMode'),
          value: modeMap[sensorMode.value],
        })
      }
    }

    // 通讯协议
    if (cfg.value.rfPacketType !== undefined) {
      if (cfg.value.rfPacketType === 0) {
        data.push({
          name: t('home.overview.cfgData.communicationProtocol'),
          value: t('home.overview.cfgData.protocolTurbo'),
        })
      } else if (cfg.value.rfPacketType === 2) {
        data.push({
          name: t('home.overview.cfgData.communicationProtocol'),
          value: t('home.overview.cfgData.protocolInitial'),
        })
      }
    }

    // 按键扫描率
    if (cfg.value.keyScanRate !== undefined) {
      const scanMap = {
        0x00: '125Hz',
        0x01: '250Hz',
        0x02: '500Hz',
        0x03: '1K',
        0x04: '2K',
        0x05: '4K',
        0x06: '8K',
      }
      if (cfg.value.keyScanRate in scanMap) {
        data.push({
          name: t('home.overview.cfgData.keyScanRate'),
          value: scanMap[cfg.value.keyScanRate],
        })
      }
    }

    // 无线射频
    if (cfg.value.rfBoostSwitch !== undefined) {
      data.push({
        name: t('home.overview.cfgData.wirelessRF'),
        value: cfg.value.rfBoostSwitch
          ? t('home.overview.cfgData.rfFull')
          : t('home.overview.cfgData.rfSmart'),
      })
    }

    // 移动同步
    if (cfg.value.motionSynSwitch !== undefined) {
      data.push({
        name: t('home.overview.cfgData.motionSync'),
        value: cfg.value.motionSynSwitch
          ? t('home.overview.cfgData.on')
          : t('home.overview.cfgData.off'),
      })
    }

    // 直线修正
    if (cfg.value.linearCorrectionSwitch !== undefined) {
      data.push({
        name: t('home.overview.cfgData.linearCorrection'),
        value: cfg.value.linearCorrectionSwitch
          ? t('home.overview.cfgData.off')
          : t('home.overview.cfgData.on'),
      })
    }

    // 波纹修正
    if (cfg.value.rippleCorrectionSwitch !== undefined) {
      data.push({
        name: t('home.overview.cfgData.rippleCorrection'),
        value: cfg.value.rippleCorrectionSwitch
          ? t('home.overview.cfgData.off')
          : t('home.overview.cfgData.on'),
      })
    }

    // 光学引擎静默高度
    if (cfg.value.silentHeight !== undefined) {
      const caps = device.value?.manifest?.capabilities || []
      const paramCap = caps.find((cap): cap is ParametersCapability => cap.type === 'parameters')
      if (paramCap?.config?.liftoffHeight) {
        const liftoffHeight = paramCap.config.liftoffHeight
        const index = cfg.value.silentHeight - 1
        let silentHeight
        if (index >= 0 && index < liftoffHeight.length) {
          silentHeight = liftoffHeight[index]
        }
        if (silentHeight != undefined) {
          data.push({
            name: t('home.overview.cfgData.silentHeight'),
            value: silentHeight + 'mm',
          })
        }
      }
    }

    // 按下去抖延迟
    if (cfg.value.keyDebounceTime !== undefined) {
      data.push({
        name: t('home.overview.cfgData.keyDebounce'),
        value: cfg.value.keyDebounceTime,
      })
    }

    // 抬起去抖延迟
    if (cfg.value.keyReleaseDebounceTime !== undefined) {
      data.push({
        name: t('home.overview.cfgData.keyReleaseDebounce'),
        value: cfg.value.keyReleaseDebounceTime,
      })
    }

    // 玻璃追踪技术
    if (cfg.value.glassSurfaceSwitch !== undefined) {
      data.push({
        name: t('home.overview.cfgData.glassTracking'),
        value: cfg.value.glassSurfaceSwitch
          ? t('home.overview.cfgData.on')
          : t('home.overview.cfgData.off'),
      })
    }

    // 传感器角度
    if (cfg.value.sensorAngle !== undefined) {
      data.push({
        name: t('home.overview.cfgData.sensorAngle'),
        value: cfg.value.sensorAngle,
      })
    }

    if (isElectronEnv && !isMacOS) {
      // 指针速度
      if (pointerSpeed.value !== undefined) {
        data.push({
          name: t('home.overview.cfgData.pointerSpeed'),
          value: pointerSpeed.value,
        })
      }

      // 滚轮速度
      if (wheelSpeed.value !== undefined) {
        const isWheelPage = wheelSpeed.value === -1
        const wheelLine = `${t('home.overview.cfgData.wheelScroll')}${wheelSpeed.value}${t('home.overview.cfgData.wheelLine')}`
        data.push({
          name: t('home.overview.cfgData.wheelSpeed'),
          value: isWheelPage ? t('home.overview.cfgData.wheelPage') : wheelLine,
        })
      }
    }

    // 休眠时间
    if (cfg.value.sleepTime !== undefined) {
      let time = cfg.value.sleepTime + t('home.overview.cfgData.minutes')
      if (cfg.value.sleepTime === 255) {
        time = t('home.overview.cfgData.noSleep')
      }
      data.push({
        name: t('home.overview.cfgData.sleepTime'),
        value: time,
      })
    }

    if (!data.length) return null

    return {
      title: t('home.overview.cfgData.title'),
      data,
    }
  })

  return {
    cfgData,
    initCfgData,
  }
}
